import { Drawer, Table, Tag, Typography } from 'antd'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import getWorkReports, {
	WorkReportRecord,
	WorkReportReviewStatus,
} from 'api/workReports/getWorkReports'
import { WorkReportsFiltersState } from 'container/WorkReportsPageLayout'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import handleFormError from 'utils/handleFormError'
import styles from './styles.module.scss'

const { Text } = Typography

type Props = {
	filters: WorkReportsFiltersState
}

const STATUS_COLORS: Record<string, string> = {
	open: 'default',
	accepted: 'success',
	rejected: 'error',
	skipped: 'warning',
}

const formatWhen = (value?: string | null) => {
	if (!value || !moment(value).isValid()) return '—'
	return moment(value).format('D MMM YYYY, h:mm A')
}

const feedbackLabel = (status?: string) => {
	const key = String(status || 'open').toLowerCase()
	if (key === 'accepted') return 'Thumbs up'
	if (key === 'rejected') return 'Thumbs down'
	if (key === 'skipped') return 'Skipped'
	return 'No feedback'
}

const WorkReportsTable: React.FC<Props> = ({ filters }) => {
	const [records, setRecords] = useState<WorkReportRecord[]>([])
	const [loading, setLoading] = useState(false)
	const [currentPage, setCurrentPage] = useState(1)
	const [pageSize, setPageSize] = useState(20)
	const [totalCount, setTotalCount] = useState(0)
	const [selected, setSelected] = useState<WorkReportRecord | null>(null)

	const pagination = useMemo(
		() => ({ current: currentPage, total: totalCount, pageSize }),
		[currentPage, totalCount, pageSize]
	)

	const load = async (page: number, size: number) => {
		setLoading(true)
		try {
			const statuses =
				filters.statuses && filters.statuses !== 'ALL'
					? String(filters.statuses)
					: undefined
			const response = await getWorkReports({
				page,
				size,
				clientId: filters.clientId,
				contactId: filters.contactId,
				q: filters.q,
				statuses,
				fromDayKey: filters.fromDayKey,
				toDayKey: filters.toDayKey,
				reviewedOnly: filters.reviewedOnly || undefined,
			})
			setRecords(response.records || [])
			setCurrentPage(response.currentPage || page)
			setTotalCount(response.totalCount || 0)
		} catch (error: any) {
			handleFormError(error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		setCurrentPage(1)
	}, [
		filters.clientId,
		filters.contactId,
		filters.q,
		filters.statuses,
		filters.fromDayKey,
		filters.toDayKey,
		filters.reviewedOnly,
	])

	useEffect(() => {
		load(currentPage, pageSize)
	}, [
		currentPage,
		pageSize,
		filters.clientId,
		filters.contactId,
		filters.q,
		filters.statuses,
		filters.fromDayKey,
		filters.toDayKey,
		filters.reviewedOnly,
	])

	const columns: ColumnsType<WorkReportRecord> = [
		{
			title: 'Client',
			dataIndex: 'clientName',
			key: 'clientName',
			width: 180,
			render: (value: string) => value || '—',
		},
		{
			title: 'Contact',
			key: 'contact',
			width: 180,
			render: (_: unknown, row) => (
				<div>
					<div>{row.contactName || '—'}</div>
					{row.mobile ? (
						<div className={styles.contactMeta}>{row.mobile}</div>
					) : null}
				</div>
			),
		},
		{
			title: 'Summary',
			dataIndex: 'shortSummary',
			key: 'shortSummary',
			render: (value: string, row) => (
				<button
					type='button'
					className={styles.summaryCell}
					onClick={() => setSelected(row)}
					style={{
						border: 'none',
						background: 'transparent',
						padding: 0,
						textAlign: 'left',
						cursor: 'pointer',
					}}
					title='Open details'
				>
					{value || row.currentSummary || '—'}
				</button>
			),
		},
		{
			title: 'Feedback',
			key: 'feedback',
			width: 280,
			render: (_: unknown, row) => {
				const status = String(
					row.summaryReviewStatus || 'open'
				) as WorkReportReviewStatus
				const note = row.summaryRejectionReason || row.summaryManualContext || ''
				return (
					<div className={styles.feedbackCell}>
						<Tag color={STATUS_COLORS[status] || 'default'}>
							{feedbackLabel(status)}
						</Tag>
						{note ? <div className={styles.feedbackNote}>{note}</div> : null}
						<div className={styles.feedbackMeta}>
							{row.summaryReviewedAt
								? `${formatWhen(row.summaryReviewedAt)}${
										row.summaryReviewedByName ? ` · ${row.summaryReviewedByName}` : ''
								  }`
								: 'Not reviewed yet'}
						</div>
					</div>
				)
			},
		},
		{
			title: 'Generated',
			dataIndex: 'generatedAt',
			key: 'generatedAt',
			width: 170,
			render: (value: string) => formatWhen(value),
		},
	]

	const handlePagination = (changes: TablePaginationConfig) => {
		const nextSize = changes.pageSize || 20
		if (nextSize !== pageSize) {
			setPageSize(nextSize)
			setCurrentPage(1)
			return
		}
		setCurrentPage(changes.current || 1)
	}

	return (
		<>
			<Table
				className={styles.table}
				columns={columns}
				dataSource={records}
				loading={loading}
				rowKey={row => row.id || `${row.clientId}-${row.contactId}`}
				pagination={{ ...pagination, position: ['bottomCenter'] }}
				onChange={handlePagination}
				onRow={row => ({
					onClick: () => setSelected(row),
					style: { cursor: 'pointer' },
				})}
			/>
			<Drawer
				visible={!!selected}
				width={520}
				title={selected ? selected.contactName || 'Work Report' : 'Work Report'}
				onClose={() => setSelected(null)}
				destroyOnClose
			>
				{selected ? (
					<>
						<div className={styles.drawerSection}>
							<p className={styles.drawerLabel}>Client</p>
							<p className={styles.drawerBody}>{selected.clientName || '—'}</p>
						</div>
						<div className={styles.drawerSection}>
							<p className={styles.drawerLabel}>Contact</p>
							<p className={styles.drawerBody}>
								{selected.contactName || '—'}
								{selected.mobile ? ` · ${selected.mobile}` : ''}
							</p>
						</div>
						<div className={styles.drawerSection}>
							<p className={styles.drawerLabel}>Feedback</p>
							<Tag
								color={
									STATUS_COLORS[String(selected.summaryReviewStatus || 'open')] ||
									'default'
								}
							>
								{feedbackLabel(selected.summaryReviewStatus)}
							</Tag>
							{selected.summaryRejectionReason ? (
								<p className={styles.drawerBody} style={{ marginTop: 8 }}>
									<strong>Rejection reason:</strong> {selected.summaryRejectionReason}
								</p>
							) : null}
							{selected.summaryManualContext ? (
								<p className={styles.drawerBody} style={{ marginTop: 8 }}>
									<strong>Comment:</strong> {selected.summaryManualContext}
								</p>
							) : null}
							<p className={styles.feedbackMeta} style={{ marginTop: 8 }}>
								{selected.summaryReviewedAt
									? `Reviewed ${formatWhen(selected.summaryReviewedAt)}${
											selected.summaryReviewedByName
												? ` by ${selected.summaryReviewedByName}`
												: ''
									  }`
									: 'Not reviewed yet'}
							</p>
						</div>
						<div className={styles.drawerSection}>
							<p className={styles.drawerLabel}>Summary</p>
							<p className={styles.drawerBody}>
								{selected.shortSummary || selected.currentSummary || '—'}
							</p>
						</div>
						{selected.customerGoal ? (
							<div className={styles.drawerSection}>
								<p className={styles.drawerLabel}>Customer goal</p>
								<p className={styles.drawerBody}>{selected.customerGoal}</p>
							</div>
						) : null}
						{selected.agentHandlingSummary ? (
							<div className={styles.drawerSection}>
								<p className={styles.drawerLabel}>Agent handling</p>
								<p className={styles.drawerBody}>{selected.agentHandlingSummary}</p>
							</div>
						) : null}
						<div className={styles.drawerSection}>
							<p className={styles.drawerLabel}>Signals</p>
							<p className={styles.drawerBody}>
								Sentiment: {selected.sentiment || '—'} · Urgency:{' '}
								{selected.urgency || '—'}
								{selected.isIssueCase ? ' · Issue case' : ''}
							</p>
							{selected.assignedAgentNames?.length ? (
								<p className={styles.drawerBody} style={{ marginTop: 6 }}>
									Agents: {selected.assignedAgentNames.join(', ')}
								</p>
							) : null}
							<Text type='secondary'>
								Generated {formatWhen(selected.generatedAt)}
							</Text>
						</div>
					</>
				) : null}
			</Drawer>
		</>
	)
}

export default WorkReportsTable
